import type { AnyActorRef } from 'xstate';
import { DOCUMENTS_STATE } from '~/features/xstateSystem';

/** The documents service only takes GET_DOCUMENTS in `$_IDLE`. */
const IDLE = '$_IDLE';

/**
 * [MCV-1753] How long a waiter holds its slot. A waiter is never dropped for being slow (a long
 * upload still ends in `$_IDLE`, and the change must be fetched then), but past this age a new
 * request for the same vault replaces it instead of being swallowed, so a stuck upload cannot
 * block every later refresh for the session.
 */
export const FORCED_REFRESH_WAIT_LIMIT_MS = 60_000;

const isStopped = (actor: AnyActorRef | undefined) => actor?.getSnapshot().status === 'stopped';

/** Reads the vault at send time: the documents view it holds can change between click and send. */
const sendForcedGet = (vault: AnyActorRef) => {
  const snapshot = vault.getSnapshot();
  if (!snapshot.context.providerId) return;
  snapshot.context.documentsService?.send({
    type: 'GET_DOCUMENTS',
    providerId: snapshot.context.providerId,
    forceRefresh: true,
    viewRef: snapshot.children?.[DOCUMENTS_STATE],
  });
};

/**
 * [MCV-1753] A documents refresh that has to see a delegate's change. Two things stand between a
 * plain `GET_DOCUMENTS` and that: the service's five-minute cache answers it without a request,
 * and a fetch, upload or parse already in flight drops it on the floor (and the fetch already
 * running may have been requested before the change). So it is sent with `forceRefresh`, and when
 * the service is busy it waits for `$_IDLE` and sends the same forced refresh then.
 *
 * One waiter at a time: a second `request` for the same vault while one is pending would only
 * stack a second fetch behind the first. But the waiter belongs to ONE vault. A request for a
 * different vault (the user signed out or switched role, so the old one was replaced) replaces it,
 * a waiter whose service stopped is dropped, and one older than FORCED_REFRESH_WAIT_LIMIT_MS is
 * replaced by the next request rather than swallowing it: the live path shares one refresher for
 * the whole session, so a waiter that never resolved would otherwise swallow every later refresh
 * and keep the old actor alive. `cancel` drops the waiter, for the owner's unmount or for the vault
 * it was queued against going away.
 */
export const createForcedDocumentsRefresher = () => {
  let waiter: { vault: AnyActorRef; startedAt: number; cancel: () => void } | null = null;

  const cancel = () => {
    waiter?.cancel();
    waiter = null;
  };

  const request = (vault: AnyActorRef) => {
    const service: AnyActorRef | undefined = vault.getSnapshot().context.documentsService;
    // Nothing to refresh on a stopped vault or service; and what was waiting on it never will.
    if (isStopped(vault) || isStopped(service)) {
      cancel();
      return;
    }
    if (service && !service.getSnapshot().matches(IDLE)) {
      if (waiter?.vault === vault && Date.now() - waiter.startedAt < FORCED_REFRESH_WAIT_LIMIT_MS) {
        return;
      }
      cancel();
      // The observers below run later, never during subscribe(), so these may refer to each other.
      const release = () => {
        subscription.unsubscribe();
        if (waiter === current) waiter = null;
      };
      const current = { vault, startedAt: Date.now(), cancel: release };
      const subscription = service.subscribe({
        next: (state) => {
          if (!state.matches(IDLE)) return;
          release();
          sendForcedGet(vault);
        },
        // The service was stopped while the waiter was queued: it will never be idle.
        complete: () => release(),
      });
      waiter = current;
      return;
    }
    // The service is idle now, so any waiter left over is for a vault that has gone.
    cancel();
    sendForcedGet(vault);
  };

  return { request, cancel };
};
